import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { LevelHistoryContent } from "../LevelHistoryDialog";

const inputCls = "w-full px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

export function StudentLevelBadges({ student, langDefs }) {
  const badges = langDefs
    .filter((d) => student.levels?.[d.subject.id])
    .map((d) => ({ code: d.subject.code, level: student.levels[d.subject.id] }));
  if (!badges.length) return null;
  return (
    <span className="flex items-center gap-1">
      {badges.map((b) => (
        <span
          key={b.code}
          className="px-1.5 py-0.5 bg-[var(--maroon)]/10 text-[var(--maroon)] text-[10px] font-mono-spec font-semibold"
          data-testid={`student-level-badge-${student.id}-${b.code}`}
        >
          {b.code}·{b.level}
        </span>
      ))}
    </span>
  );
}

function LevelSelectFields({ langDefs, values, setValues }) {
  return (
    <>
      {langDefs.map((d) => (
        <div key={d.subject.id}>
          <label className="eyebrow block mb-1">{d.subject.name}</label>
          <select
            className={inputCls}
            value={values[d.subject.id] || ""}
            onChange={(e) => setValues((v) => ({ ...v, [d.subject.id]: e.target.value }))}
            data-testid={`student-level-select-${d.subject.code}`}
          >
            <option value="">Non inscrit / Non concerné</option>
            {d.levels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
          </select>
        </div>
      ))}
    </>
  );
}

export default function StudentLevelsDialog({ student, langDefs, onClose, onSaved }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues(Object.fromEntries(langDefs.map((d) => [d.subject.id, student.levels?.[d.subject.id] || ""])));
  }, [student, langDefs]);

  const save = async () => {
    setBusy(true);
    try {
      for (const d of langDefs) {
        await api.put(`/admin/students/${student.id}/levels`, {
          subject_id: d.subject.id,
          level: values[d.subject.id] || null,
        });
      }
      toast.success("Niveaux de langue mis à jour");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="student-levels-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Niveaux de langues</DialogTitle>
          <DialogDescription>
            {student.last_name.toUpperCase()} {student.first_name} — le niveau est indépendant de la classe et modifiable en cours d'année.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <LevelSelectFields langDefs={langDefs} values={values} setValues={setValues} />
          <div className="pt-3 border-t border-[var(--parchment-2)]">
            <div className="eyebrow mb-2">Historique des changements</div>
            <LevelHistoryContent studentId={student.id} />
          </div>
        </div>
        <DialogFooter>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="student-levels-save-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
